import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, MessageSquareOff, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchAllChannels } from "../../services/adminService";
import { api1 } from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import Modal from "../../components/modal/Modal";
import SecondaryLoader from "../../components/SecondaryLoader/SecondaryLoader";
import WelcomeCard from "../../components/WelcomeCard";
import { toast } from "react-toastify";

const ListGroup = () => {
  const statusColors = {
    industry: "bg-pending text-white",
    learning: "bg-accept text-white",
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [groupData, setGroupData] = useState(null);
  const userId = useSelector((state) => state.auth.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const formattedDate = (date) => {
    const fdate = new Date(date);
    return fdate.toLocaleString();
  };

  const {
    data: fetchAllGroupsData,
    isLoading: fetchAllGroupsIsLoading,
    error: fetchAllGroupsError,
  } = useQuery({
    queryKey: ["totalGroups"],
    queryFn: fetchAllChannels,
    enabled: !!userId,
  });

  if (fetchAllGroupsIsLoading) {
    return <SecondaryLoader />;
  }

  if (fetchAllGroupsError) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 text-center bg-gray-50 rounded-lg shadow-inner border border-gray-200">
        <MessageSquareOff className="w-12 h-12 text-red-500 mb-4" />
        <h3 className="text-xl font-semibold text-gray-700">
          Error Fetching Groups
        </h3>
        <p className="text-sm text-gray-500 mt-2">
          There was an error fetching the groups. Please try again later.
        </p>
      </div>
    );
  }

  const handleModal = (group) => {
    setGroupData(group);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setGroupData(null);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await api1.delete(API_ENDPOINTS.DELETE_GROUP(groupData._id));
      if (response.data?.success) {
        toast.success("Group deleted successfully");
        queryClient.invalidateQueries(["totalGroups"]);
      } else {
        toast.error("Failed to delete group");
      }
    } catch (error) {
      console.error("Error deleting group:", error);
      toast.error("Failed to delete group");
    } finally {
      setModalOpen(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={modalOpen}
        onClose={handleModalClose}
        width="w-[400px] md:w-[600px]"
      >
        <div className="flex flex-col items-center justify-center gap-4 p-4">
          <div className="text-red-600">
            <AlertTriangle className="w-12 h-12" />
          </div>

          <h2 className="text-lg md:text-xl font-semibold text-center text-gray-800">
            Are you sure you want to delete this group?
          </h2>
          <p className="text-sm text-gray-500 text-center">
            This action cannot be undone.
          </p>

          <div className="flex justify-center gap-4 mt-4 w-full">
            <button
              onClick={handleModalClose}
              className="px-4 py-2 w-full border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 w-full bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
      <div className="flex flex-col gap-6">
        <WelcomeCard title={"Groups List"} />
        {fetchAllGroupsData?.data?.length > 0 ? (
          <div className="p-4 sm:p-6 bg-white min-h-screen rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              Groups List
            </h3>

            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead className="border-y bg-gray-100">
                  <tr className="uppercase text-xs sm:text-sm text-left text-gray-600">
                    <th className="py-3 px-4">Name</th>
                    <th className="py-3 px-4">Created Date</th>
                    <th className="py-3 px-4">Description</th>
                    <th className="py-3 px-4 text-center">Type</th>
                    <th className="py-3 px-4 text-center">Participants</th>
                    <th className="py-3 px-4 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {fetchAllGroupsData?.data?.map((group, index) => (
                      <tr
                        key={group._id || index}
                        className="border-b hover:bg-gray-50 transition"
                      >
                        <td className="py-4 px-4">
                          <div className="font-medium text-gray-800">
                            {group.name}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-gray-600">
                          {formattedDate(group.createdAt)}
                        </td>
                        <td className="py-4 px-4 text-gray-600">
                          {group.description || "No description"}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span
                            className={`px-3 py-1 rounded text-sm font-semibold ${
                              statusColors[group.groupType] || "bg-gray-500 text-white"
                            }`}
                          >
                            {group.groupType || "N/A"}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center text-gray-600">
                          {group.participants?.length || 0}
                        </td>
                        <td className="py-4 px-6 text-center">
                          <div className="flex items-center justify-center gap-3">
                            <button
                              onClick={() =>
                                navigate(
                                  `/dashboard/groups/create/${group._id}`,
                                  {
                                    state: {
                                      groupData: group,
                                    },
                                  }
                                )
                              }
                              className="text-white text-sm font-medium bg-blue-500 hover:bg-blue-600 flex items-center gap-2 px-4 py-2 rounded-md transition-all duration-200 shadow-sm hover:shadow"
                            >
                              <Pencil className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleModal(group)}
                              className="text-white text-sm font-medium bg-red-500 hover:bg-red-600 flex items-center gap-2 px-4 py-2 rounded-md transition-all duration-200 shadow-sm hover:shadow"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 px-4 text-center bg-gray-50 rounded-lg shadow-inner border border-gray-200">
            <MessageSquareOff className="w-12 h-12 text-indigo-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700">
              No Groups Found
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              You haven't created any groups yet.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default ListGroup;
